import { useNavigate } from 'react-router-dom';
import useGameStore from '@/store/gameStore';
import websocketService from '@/services/websocket';
import toast from 'react-hot-toast';

function GameControls() {
  const navigate = useNavigate();
  const { gameState, isHost, user } = useGameStore();

  if (!gameState) return null;

  const humanPlayerCount = Object.values(gameState.players).filter((p) => !p.isAi).length;
  const totalPlayers = humanPlayerCount + gameState.config.aiPlayerCount;
  const canStartGame = isHost() && gameState.phase === 'LOBBY' && totalPlayers >= 4;

  const handleStartGame = () => {
    if (!canStartGame) {
      toast.error('Need at least 4 players to start');
      return;
    }
    websocketService.startGame();
  };

  const handleNextRound = () => {
    websocketService.nextRound();
  };

  const handleSkipCard = () => {
    websocketService.skipCard();
  };

  const handleRestartGame = () => {
    if (window.confirm('Are you sure you want to restart the game?')) {
      websocketService.send({
        type: 'RESTART_GAME',
        timestamp: Date.now(),
      });
    }
  };

  const handleLeaveGame = () => {
    if (window.confirm('Are you sure you want to leave this game?')) {
      websocketService.disconnect();
      navigate('/lobby');
      toast.success('Left the game');
    }
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">Game Controls</h2>

      <div className="space-y-3">
        {/* Start Game Button */}
        {gameState.phase === 'LOBBY' && isHost() && (
          <button
            onClick={handleStartGame}
            disabled={!canStartGame}
            data-testid="btn-start-game"
            className="w-full btn-primary"
          >
            {canStartGame ? 'Start Game' : `Need ${4 - totalPlayers} more players`}
          </button>
        )}

        {/* Skip Card Button */}
        {gameState.phase === 'GUESSING' && gameState.round?.activeGuesserId === user?.id && (
          <button
            onClick={handleSkipCard}
            data-testid="btn-skip-card"
            className="w-full btn-danger"
          >
            Skip Card (Pass)
          </button>
        )}

        {/* Next Round Button */}
        {gameState.phase === 'SCORING' && isHost() && (
          <button
            onClick={handleNextRound}
            data-testid="btn-next-round"
            className="w-full btn-primary"
          >
            Next Round
          </button>
        )}

        {/* Restart Game Button */}
        {gameState.phase === 'GAME_OVER' && isHost() && (
          <button
            onClick={handleRestartGame}
            data-testid="btn-restart-game"
            className="w-full btn-primary"
          >
            Play Again
          </button>
        )}

        {/* Game Settings (Lobby Only) */}
        {gameState.phase === 'LOBBY' && isHost() && (
          <div className="space-y-3 pt-3 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
              <select
                value={gameState.config.difficulty}
                onChange={(e) => {
                  websocketService.updateSettings({
                    difficulty: e.target.value as 'NORMAL' | 'HARD',
                  });
                }}
                data-testid="select-difficulty"
                className="input"
              >
                <option value="NORMAL">Normal</option>
                <option value="HARD">Hard</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Rounds</label>
              <input
                type="number"
                min="5"
                max="20"
                value={gameState.config.totalRounds}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (value >= 5 && value <= 20) {
                    websocketService.updateSettings({ totalRounds: value });
                  }
                }}
                data-testid="input-total-rounds"
                className="input"
              />
            </div>
          </div>
        )}

        {/* Leave Game Button */}
        <div className="pt-3 border-t border-gray-200">
          <button
            onClick={handleLeaveGame}
            data-testid="btn-leave-game"
            className="w-full btn-secondary text-danger-600 hover:bg-danger-50"
          >
            Leave Game
          </button>
        </div>

        {/* Debug Mode (Dev Only) */}
        {import.meta.env.DEV && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={async () => {
                try {
                  const api = await import('@/services/api');
                  const debugData = await api.default.getDebugState(gameState.roomId);
                  // eslint-disable-next-line no-console
                  console.log('Debug State:', debugData);
                  toast.success('Debug data logged to console');
                } catch (error) {
                  console.error('Debug error:', error);
                  toast.error('Failed to fetch debug data');
                }
              }}
              className="w-full btn-secondary text-sm"
              data-testid="btn-debug"
            >
              🔍 Debug State (Console)
            </button>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-6 p-3 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Current Phase:</h3>
        <p className="text-xs text-gray-600">
          {gameState.phase === 'LOBBY' && 'Waiting for players to join...'}
          {gameState.phase === 'WRITING' && 'Write a one-word clue for the mystery word!'}
          {gameState.phase === 'REVIEW' && 'Checking for duplicate clues...'}
          {gameState.phase === 'GUESSING' && 'The guesser is trying to find the word!'}
          {gameState.phase === 'SCORING' && 'Review the results of this round.'}
          {gameState.phase === 'GAME_OVER' && 'The game has ended. Check your final score!'}
        </p>
      </div>
    </div>
  );
}

export default GameControls;
